import { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronUp, ChevronDown, Activity, Table, AlertCircle, Terminal, Minimize2, Maximize2, Brain, ArrowLeftRight, ArrowRight, Columns3, Loader2, X, Copy, Check, Pin, PinOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useReactFlow } from 'reactflow';
import { apiGet, apiFetch } from '@/api';
import { formatToLocalTime24h } from '@/utils/dateTime';

interface BottomPanelProps {
    logs: any[];
    nodeStats: Record<string, any>;
    previews?: Record<string, any[]>;
    selectedNodeId?: string | null;
    selectedNodeLabel?: string;
    isRunning: boolean;
    isOpen: boolean;
    onToggle: () => void;
    isPinned?: boolean;
    onTogglePin?: () => void;
    consoleSize?: { height: number; width: number };
    onResize?: (size: { height: number; width: number }) => void;
}

interface SchemaColumn {
    name: string;
    type: string;
    source?: string; // 'left' | 'right' for join nodes
}

/**
 * Resolves the output schema for a given node by walking the graph recursively.
 * This is a design-time operation — no execution needed.
 */
async function resolveSchema(
    nodeId: string,
    getNodes: () => any[],
    getEdges: () => any[],
    visited: Set<string> = new Set()
): Promise<SchemaColumn[]> {
    // Circuit breaker for cycles
    if (visited.has(nodeId)) return [];
    visited.add(nodeId);

    const nodes = getNodes();
    const edges = getEdges();
    const targetNode = nodes.find((n: any) => n.id === nodeId);
    if (!targetNode) return [];

    // ─── Source Nodes ───
    const type = targetNode.type?.toLowerCase();

    if (type === 'source') {
        // Prefer stored columns (already fetched during config)
        if (targetNode.data.outputSchema && Array.isArray(targetNode.data.outputSchema)) {
            return targetNode.data.outputSchema.map((c: any) => ({
                name: c.name, type: c.dataType || c.type || 'string'
            }));
        }
        if (targetNode.data.columns && Array.isArray(targetNode.data.columns)) {
            return targetNode.data.columns.map((c: any) => ({
                name: c.name, type: c.dataType || c.type || 'string'
            }));
        }
        // Fallback: fetch from API
        // Data Model Support
        const dataModelId = targetNode.data.dataModelId || (targetNode.data.connectionMode === 'dataset' ? targetNode.data.datasetId : null);
        if (dataModelId) {
            try {
                const model = await apiGet(`/datamodels/${dataModelId}`);
                if (model && Array.isArray(model.tables)) {
                    const table = model.tables.find((t: any) => t.name === targetNode.data.table);
                    if (table?.columns) {
                        return table.columns.map((c: any) => ({
                            name: c.name, type: c.data_type || 'string'
                        }));
                    }
                }
            } catch (e) {
                console.error('[resolveSchema] DataModel fetch failed:', e);
            }
        }

        // Datasource Support
        // Use dataSourceId if available, or fall back to datasetId if it wasn't a Data Model (legacy)
        const dsId = targetNode.data.dataSourceId || (!dataModelId ? targetNode.data.datasetId : null);
        if (dsId) {
            try {
                const schema = await apiGet(`/datasources/${dsId}/schema`);

                if (Array.isArray(schema)) {
                    if (targetNode.data.table) {
                        const table = schema.find((t: any) => t.name === targetNode.data.table);
                        if (table?.columns) {
                            return table.columns.map((c: any) => ({
                                name: c.name, type: c.dataType || c.type || 'string'
                            }));
                        }
                    }
                    // Dataset fallback / First table fallback
                    if (schema.length > 0 && schema[0]?.columns) {
                        return schema[0].columns.map((c: any) => ({
                            name: c.name, type: c.dataType || c.type || 'string'
                        }));
                    }
                }
            } catch (e) {
                console.error('[resolveSchema] Datasource fetch failed:', e);
            }
        }
        return [];
    }

    // EDI Source (hardcoded standard)
    if (type === 'edisource') {
        return [
            { name: 'ClaimID', type: 'string' },
            { name: 'PayerName', type: 'string' },
            { name: 'PayeeName', type: 'string' },
            { name: 'ClaimStatus', type: 'string' },
            { name: 'TotalChargeAmount', type: 'double' },
            { name: 'TotalPaymentAmount', type: 'double' },
            { name: 'PatientControlNumber', type: 'string' },
            { name: 'PayerControlNumber', type: 'string' },
            { name: 'ServiceDate', type: 'date' },
            { name: 'DenialReasonCode', type: 'string' },
            { name: 'DenialDescription', type: 'string' }
        ];
    }

    // ─── Multi-Input Nodes ───
    if (type === 'join') {
        const incoming = edges.filter((e: any) => e.target === nodeId);
        if (incoming.length < 2) {
            if (incoming.length === 1) return resolveSchema(incoming[0].source, getNodes, getEdges, visited);
            return [];
        }
        const leftEdge = incoming.find((e: any) => e.targetHandle === 'left') || incoming[0];
        const rightEdge = incoming.find((e: any) => e.targetHandle === 'right' && e.id !== leftEdge.id) || incoming[1];
        const s1 = await resolveSchema(leftEdge.source, getNodes, getEdges, new Set(visited));
        const s2 = await resolveSchema(rightEdge.source, getNodes, getEdges, new Set(visited));
        return [
            ...s1.map(c => ({ ...c, source: 'left' })),
            ...s2.map(c => ({ ...c, source: 'right' }))
        ];
    }

    if (type === 'union') {
        if (targetNode.data.columns?.length > 0) {
            return targetNode.data.columns.map((c: any) => ({ name: c.name, type: c.type || 'string' }));
        }
        const incoming = edges.filter((e: any) => e.target === nodeId);
        if (incoming.length > 0) return resolveSchema(incoming[0].source, getNodes, getEdges, visited);
        return [];
    }

    // ─── Multi-Join: merge schemas from ALL inputs, prefer post-execution outputSchema ───
    if (type === 'multijoin' || type === 'multi_join') {
        // After execution, outputSchema is populated from previewData
        if (targetNode.data.outputSchema && Array.isArray(targetNode.data.outputSchema) && targetNode.data.outputSchema.length > 0) {
            return targetNode.data.outputSchema.map((c: any) => ({
                name: c.name, type: c.dataType || c.type || 'string'
            }));
        }
        // Pre-execution: merge all incoming schemas
        const incoming = edges.filter((e: any) => e.target === nodeId);
        const seen = new Set<string>();
        const merged: SchemaColumn[] = [];
        for (const edge of incoming) {
            const schema = await resolveSchema(edge.source, getNodes, getEdges, new Set(visited));
            schema.forEach(c => {
                if (!seen.has(c.name)) { seen.add(c.name); merged.push(c); }
            });
        }
        return merged;
    }

    // ─── Single-Input Transform Nodes ───
    const incoming = edges.find((e: any) => e.target === nodeId);
    if (!incoming) {
        // Check if inside a container (Loop)
        const pId = targetNode.parentId || targetNode.parentNode;
        if (pId) {
            const pNode = nodes.find(n => n.id === pId);
            if (pNode && (pNode.type?.toLowerCase() === 'loop' || pNode.type?.toLowerCase() === 'container')) {
                return await resolveSchema(pId, getNodes, getEdges, visited);
            }
        }
        return [];
    }

    let parentSchema = await resolveSchema(incoming.source, getNodes, getEdges, visited);

    // Apply transforms based on node type
    // type is already defined at top of function

    if (type === 'column' && targetNode.data.columns) {
        const newSchema: SchemaColumn[] = [];
        targetNode.data.columns.forEach((col: any) => {
            if (col.keep !== false) {
                const original = parentSchema.find(c => c.name === col.name);
                newSchema.push({
                    name: col.newName || col.name,
                    type: (col.type && col.type !== 'AUTO') ? col.type : (original?.type || 'string')
                });
            }
        });
        if (newSchema.length > 0) return newSchema;
    }
    else if (type === 'aggregate') {
        const groups = targetNode.data.groups || [];
        const aggs = targetNode.data.aggregates || [];
        const schema = parentSchema.filter(c => groups.includes(c.name));
        aggs.forEach((a: any) => {
            schema.push({ name: a.alias || `${a.func}_${a.column}`, type: 'double' });
        });
        return schema;
    }
    else if (type === 'formula' && targetNode.data.formulas) {
        const clone = parentSchema.map(c => ({ ...c }));
        targetNode.data.formulas.forEach((f: any) => {
            if (f.column) {
                const existing = clone.find(c => c.name === f.column);
                if (existing) existing.type = f.dataType || 'string';
                else clone.push({ name: f.column, type: f.dataType || 'string' });
            }
        });
        return clone;
    }
    else if (type === 'unpivot') {
        const uCols = targetNode.data.dataColumns || targetNode.data.unpivotCols || [];
        const gCols = targetNode.data.groupColumns || [];
        let keptColumns = gCols.length > 0
            ? parentSchema.filter(c => gCols.includes(c.name))
            : parentSchema.filter(c => !uCols.includes(c.name));
        return [
            ...keptColumns,
            { name: targetNode.data.nameHeader || 'Attribute', type: 'string' },
            { name: targetNode.data.valueHeader || 'Value', type: 'any' }
        ];
    }
    else if (type === 'pivot') {
        const groups = targetNode.data.groups || [];
        let schema = groups.length > 0 ? parentSchema.filter(c => groups.includes(c.name)) : [...parentSchema];
        if (targetNode.data.pivotValues) {
            targetNode.data.pivotValues.split(',').map((v: string) => v.trim()).filter(Boolean).forEach((val: string) => {
                schema.push({ name: val, type: 'any' });
            });
        }
        return schema;
    }
    else if (type === 'window') {
        const clone = [...parentSchema];
        const funcs = targetNode.data.windowFunctions || [];
        funcs.forEach((f: any) => {
            const colName = f.targetColumn || f.alias || `${f.function}_${f.argumentColumn}`;
            if (colName && !clone.find(c => c.name === colName)) {
                clone.push({ name: colName, type: 'double' });
            }
        });
        return clone;
    }
    else if (type === 'pareto') {
        return [
            ...parentSchema,
            { name: '_Pareto_CumSum', type: 'double' },
            { name: '_Pareto_Total', type: 'double' },
            { name: '_Pareto_Pct', type: 'double' },
            { name: '_Pareto_Segment', type: 'string' }
        ];
    }
    else if (type === 'scoremodel') {
        return [...parentSchema, { name: 'Score', type: 'double' }];
    }
    else if (type === 'aitransform' || type === 'ai_transform') {
        const newCol = targetNode.data.generatedColumn || targetNode.data.outputColumn || 'transformed_data';
        if (!parentSchema.find(c => c.name === newCol)) {
            return [...parentSchema, { name: newCol, type: 'any' }];
        }
    }
    else if (type === 'aiinsight' || type === 'ai_insight') {
        const newCol = targetNode.data.outputColumn || 'ai_insight';
        if (!parentSchema.find(c => c.name === newCol)) {
            return [...parentSchema, { name: newCol, type: 'string' }];
        }
    }
    else if (type === 'llmnode' || type === 'llm_node' || type === 'aiagent' || type === 'ai_agent') {
        const newCol = targetNode.data.outputColumn || (type.includes('agent') ? 'agent_result' : 'llm_response');
        if (!parentSchema.find(c => c.name === newCol)) {
            return [...parentSchema, { name: newCol, type: 'string' }];
        }
    }
    else if (type === 'getvariable' || type === 'get_variable') {
        // Get variable appends the target variable as a new column to the dataset
        const newCol = targetNode.data.outputColumn || targetNode.data.variableName;
        if (newCol && !parentSchema.find(c => c.name === newCol)) {
            // Flow variables can be any type, default to string/any
            return [...parentSchema, { name: newCol, type: 'any' }];
        }
    }
    else if (
        type === 'setvariable' || type === 'set_variable' || 
        type === 'conditionalrouter' || type === 'conditional_router' ||
        type === 'pythonscript' || type === 'python_script'
    ) {
        // Set Variable, Router, and typical Script nodes pass inputs directly through
        // without altering the basic output schema shape (unless the script adds things, 
        // but by default we pass parentSchema).
        return parentSchema;
    }

    // Default: passthrough (filter, clean, sort, deduplicate, etc.)
    return parentSchema;
}

/** Compute the diff status of each output column relative to input */
function computeDiffStatus(
    input: SchemaColumn[],
    output: SchemaColumn[],
    nodeType: string
): Map<string, 'unchanged' | 'renamed' | 'added' | 'type_changed'> {
    const result = new Map<string, 'unchanged' | 'renamed' | 'added' | 'type_changed'>();
    const inputNames = new Set(input.map(c => c.name));

    for (const col of output) {
        if (inputNames.has(col.name)) {
            const inputCol = input.find(c => c.name === col.name);
            if (inputCol && inputCol.type !== col.type) {
                result.set(col.name, 'type_changed');
            } else {
                result.set(col.name, 'unchanged');
            }
        } else {
            // Check if this was renamed from an input column
            if (nodeType === 'column') {
                result.set(col.name, 'renamed');
            } else {
                result.set(col.name, 'added');
            }
        }
    }
    return result;
}

/** Find columns that were dropped (in input but not in output) */
function findDroppedColumns(input: SchemaColumn[], output: SchemaColumn[]): SchemaColumn[] {
    const outNames = new Set(output.map(c => c.name));
    return input.filter(c => !outNames.has(c.name));
}

// ─── Type Badge Colors ───
function typeBadgeColor(type: string): string {
    const t = type?.toLowerCase() || '';
    if (t.includes('int') || t.includes('bigint') || t.includes('double') || t.includes('float') || t.includes('decimal') || t.includes('numeric'))
        return 'bg-blue-100 text-blue-700';
    if (t.includes('varchar') || t.includes('string') || t.includes('text') || t.includes('char'))
        return 'bg-emerald-100 text-emerald-700';
    if (t.includes('date') || t.includes('time') || t.includes('timestamp'))
        return 'bg-amber-100 text-amber-700';
    if (t.includes('bool'))
        return 'bg-purple-100 text-purple-700';
    return 'bg-gray-100 text-gray-600';
}

export default function BottomPanel({ 
    logs, nodeStats, previews, selectedNodeId, selectedNodeLabel, isRunning, isOpen, onToggle,
    isPinned = true, onTogglePin, consoleSize, onResize 
}: BottomPanelProps) {
    const [activeTab, setActiveTab] = useState<'preview' | 'stats' | 'logs' | 'errors' | 'io'>('logs');
    const [explainResult, setExplainResult] = useState<any[] | null>(null);
    const [isExplainOpen, setIsExplainOpen] = useState(false);
    const [isCompact, setIsCompact] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const { getNodes, getEdges } = useReactFlow();

    // Responsive width observer for compact tabs
    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                // If console width is less than 600px, enable compact tabs
                setIsCompact(entry.contentRect.width < 600);
            }
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    // Resizing logic
    const isDraggingRef = useRef(false);
    const dragStart = useRef({ x: 0, y: 0, w: 0, h: 0 });

    const handleMouseDown = (e: React.MouseEvent) => {
        if (!onResize || !consoleSize) return;
        isDraggingRef.current = true;
        dragStart.current = {
            x: e.clientX,
            y: e.clientY,
            w: consoleSize.width,
            h: consoleSize.height
        };
        document.body.style.cursor = isPinned ? 'ns-resize' : 'ew-resize';
        document.body.style.userSelect = 'none';

        const onMove = (moveEvent: MouseEvent) => {
            if (!isDraggingRef.current) return;
            if (isPinned) {
                const deltaY = dragStart.current.y - moveEvent.clientY;
                const newHeight = Math.max(100, Math.min(dragStart.current.h + deltaY, window.innerHeight * 0.8));
                onResize({ ...consoleSize, height: newHeight });
            } else {
                const deltaX = dragStart.current.x - moveEvent.clientX;
                const newWidth = Math.max(250, Math.min(dragStart.current.w + deltaX, window.innerWidth * 0.8));
                onResize({ ...consoleSize, width: newWidth });
            }
        };

        const onUp = () => {
            isDraggingRef.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    const handleToggleExpand = () => {
        if (!onResize || !consoleSize) return;
        if (isPinned) {
            onResize({ ...consoleSize, height: consoleSize.height === 250 ? 500 : 250 });
        } else {
            onResize({ ...consoleSize, width: consoleSize.width === 350 ? 600 : 350 });
        }
    };
    const isExpanded = isPinned ? (consoleSize?.height || 250) > 250 : (consoleSize?.width || 350) > 350;

    // I/O Schema State
    const [ioInput, setIoInput] = useState<SchemaColumn[]>([]);
    const [ioOutput, setIoOutput] = useState<SchemaColumn[]>([]);
    const [ioLoading, setIoLoading] = useState(false);
    const [ioNodeType, setIoNodeType] = useState('');
    const [ioError, setIoError] = useState('');
    const [cellModal, setCellModal] = useState<{ column: string; value: string } | null>(null);

    // Resolve I/O Schema when node selection changes or when I/O tab is selected
    const resolveIO = useCallback(async (nodeId: string) => {
        setIoLoading(true);
        setIoError('');
        try {
            const nodes = getNodes();
            const edges = getEdges();
            const node = nodes.find((n: any) => n.id === nodeId);
            if (!node) {
                setIoInput([]);
                setIoOutput([]);
                setIoNodeType('');
                return;
            }

            setIoNodeType(node.type || '');

            // Output = resolveSchema for this node
            const output = await resolveSchema(nodeId, getNodes, getEdges);

            // Input = resolveSchema for parent(s)
            let input: SchemaColumn[] = [];
            if (node.type === 'source' || node.type === 'ediSource') {
                // Source nodes have no input
                input = [];
            } else if (node.type === 'join') {
                // For join, input is the combined schemas of both parents
                const incoming = edges.filter((e: any) => e.target === nodeId);
                const leftEdge = incoming.find((e: any) => e.targetHandle === 'left') || incoming[0];
                const rightEdge = incoming.find((e: any) => e.targetHandle === 'right' && e.id !== leftEdge?.id) || incoming[1];
                if (leftEdge) {
                    const leftSchema = await resolveSchema(leftEdge.source, getNodes, getEdges);
                    input.push(...leftSchema.map(c => ({ ...c, source: 'left' })));
                }
                if (rightEdge) {
                    const rightSchema = await resolveSchema(rightEdge.source, getNodes, getEdges);
                    input.push(...rightSchema.map(c => ({ ...c, source: 'right' })));
                }
            } else if (node.type === 'multijoin' || node.type === 'multi_join' || node.type === 'MULTIJOIN') {
                // For multi-join, input = all incoming schemas merged
                const incoming = edges.filter((e: any) => e.target === nodeId);
                for (const edge of incoming) {
                    const schema = await resolveSchema(edge.source, getNodes, getEdges);
                    input.push(...schema);
                }
            } else {
                // Single-input nodes: resolve the parent
                const incoming = edges.find((e: any) => e.target === nodeId);
                if (incoming) {
                    input = await resolveSchema(incoming.source, getNodes, getEdges);
                } else {
                    // Check if inside a container
                    const pId = node.parentId || node.parentNode;
                    if (pId) {
                        const pNode = nodes.find(n => n.id === pId);
                        if (pNode && (pNode.type?.toLowerCase() === 'loop' || pNode.type?.toLowerCase() === 'container')) {
                            input = await resolveSchema(pId, getNodes, getEdges);
                        }
                    }
                }
            }

            setIoInput(input);
            setIoOutput(output);
        } catch (e: any) {
            console.error('I/O resolution failed:', e);
            setIoError(e.message || 'Failed to resolve schema');
        } finally {
            setIoLoading(false);
        }
    }, [getNodes, getEdges]);

    // Auto-resolve when node changes or I/O tab activated
    useEffect(() => {
        if (selectedNodeId && activeTab === 'io' && isOpen) {
            resolveIO(selectedNodeId);
        }
    }, [selectedNodeId, activeTab, isOpen]);

    // Toggle Collapse
    if (!isOpen) {
        return (
            <div
                id="flow-console-toggle"
                className="h-8 bg-white border-t border-gray-200 flex items-center justify-between px-4 cursor-pointer hover:bg-gray-50 transition-colors z-20"
                onClick={onToggle}>
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-700">
                    <Terminal className="h-4 w-4 text-blue-500" />
                    <span>Execution Console</span>
                    {isRunning && <span className="flex items-center gap-1 text-[10px] text-blue-600 animate-pulse">Running...</span>}
                </div>
                <ChevronUp className="h-4 w-4 text-gray-400" />
            </div>
        );
    }

    const currentPreview = selectedNodeId && previews ? previews[selectedNodeId] : null;
    const allPreviewColumns = currentPreview && currentPreview.length > 0 ? Object.keys(currentPreview[0]) : [];

    // Get selected node — must be declared before previewColumns IIFE
    const selectedNode = selectedNodeId ? getNodes().find((n: any) => n.id === selectedNodeId) : null;

    // Filter preview columns to honour the user's column selection on the source node
    const previewColumns = (() => {
        if (!selectedNode || allPreviewColumns.length === 0) return allPreviewColumns;
        const nodeData = selectedNode.data || {};
        const selectedCols: string[] = Array.isArray(nodeData.selectedColumns) ? nodeData.selectedColumns : [];
        const outputSchema: { name: string }[] = Array.isArray(nodeData.outputSchema) ? nodeData.outputSchema : [];

        // selectedColumns: non-empty array means a subset was chosen
        if (selectedCols.length > 0) {
            const allowed = new Set(selectedCols);
            return allPreviewColumns.filter(c => allowed.has(c));
        }
        // outputSchema: use its names as the allowed set (handles filtered schema from column selector)
        if (outputSchema.length > 0) {
            const allowed = new Set(outputSchema.map(c => c.name));
            // only filter if the schema is a real subset (fewer than all DB columns)
            const filtered = allPreviewColumns.filter(c => allowed.has(c));
            if (filtered.length > 0 && filtered.length < allPreviewColumns.length) return filtered;
        }
        return allPreviewColumns;
    })();

    const showExplain = previewColumns.includes("PredictedLabel") || previewColumns.includes("Score");

    const handleExplain = async (row: any) => {
        if (!selectedNodeId) return;
        const edges = getEdges();
        const incomingEdges = edges.filter((e: any) => e.target === selectedNodeId);
        const nodes = getNodes();
        let modelEdge = incomingEdges.find((e: any) => {
            const sourceNode = nodes.find((n: any) => n.id === e.source);
            return sourceNode?.type === 'trainModel' || sourceNode?.data?.label?.toLowerCase().includes('train');
        });
        if (!modelEdge) {
            modelEdge = incomingEdges.find((e: any) => e.targetHandle && e.targetHandle.toLowerCase().includes('model'));
        }
        if (!modelEdge) {
            alert("Could not find the Connected Model Node.");
            return;
        }
        const modelNodeId = modelEdge.source;
        try {
            const data = await apiFetch('/flows/explain-prediction', null, {
                method: 'POST',
                body: JSON.stringify({ ModelNodeId: modelNodeId, RowData: row })
            });
            if (data) {
                setExplainResult(Array.isArray(data) ? data : []);
                setIsExplainOpen(true);
            } else {
                alert("Explain failed");
            }
        } catch (e) { console.error(e); alert("Explain failed"); }
    };

    const sortedStats = Object.entries(nodeStats).sort(([, a], [, b]) => {
        return new Date(a.startTime || 0).getTime() - new Date(b.startTime || 0).getTime();
    });

    // Diff computations for I/O
    const diffMap = computeDiffStatus(ioInput, ioOutput, ioNodeType);
    const droppedCols = findDroppedColumns(ioInput, ioOutput);

    // Get selected node's display name
    const nodeName = selectedNode?.data?.label || selectedNodeLabel || selectedNodeId || '';
    const nodeType = selectedNode?.type || '';

    return (
        <div
            id="flow-console-panel"
            ref={containerRef}
            className={`bg-white shadow-xl flex flex-col z-20 transition-all ease-in-out relative ${isPinned ? 'border-t border-gray-200 w-full' : 'border-l border-gray-200 h-full'}`}
            style={isPinned ? { height: consoleSize?.height || 250 } : { width: consoleSize?.width || 350 }}
        >
            {/* Resize Handles */}
            <div 
                className={`absolute ${isPinned ? 'top-0 left-0 right-0 h-1 cursor-ns-resize -translate-y-0.5' : 'left-0 top-0 bottom-0 w-1 cursor-ew-resize -translate-x-0.5'} hover:bg-blue-400 z-50 rounded bg-transparent transition-colors`}
                onMouseDown={handleMouseDown}
            />

            {/* Header / Tabs */}
            <div className="h-9 border-b border-gray-200 flex items-center justify-between bg-gray-50/80 px-2 select-none">
                <div className="flex items-center gap-1 h-full overflow-hidden whitespace-nowrap">
                    <TabButton id="flow-tab-logs" active={activeTab === 'logs'} onClick={() => setActiveTab('logs')} icon={Terminal} label="Console" isCompact={isCompact} />
                    <TabButton id="flow-tab-preview" active={activeTab === 'preview'} onClick={() => setActiveTab('preview')} icon={Table} label="Data Preview" isCompact={isCompact} />
                    <TabButton id="flow-tab-io" active={activeTab === 'io'} onClick={() => setActiveTab('io')} icon={ArrowLeftRight} label="I/O" isCompact={isCompact} />
                    <TabButton id="flow-tab-stats" active={activeTab === 'stats'} onClick={() => setActiveTab('stats')} icon={Activity} label="Statistics" isCompact={isCompact} />
                    <TabButton id="flow-tab-errors" active={activeTab === 'errors'} onClick={() => setActiveTab('errors')} icon={AlertCircle} label="Errors" count={logs.filter(l => l.level === 'Error').length} isCompact={isCompact} />
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    {onTogglePin && (
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-400 hover:text-gray-800" onClick={onTogglePin} title={isPinned ? 'Unpin to right' : 'Pin to bottom'}>
                            {isPinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                        </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-400 hover:text-gray-800" onClick={handleToggleExpand} title="Expand/Collapse">
                        {isExpanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
                    </Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-400 hover:text-gray-800" onClick={onToggle} title="Hide Console">
                        <ChevronDown className="h-3.5 w-3.5" />
                    </Button>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-hidden p-0 relative">
                {/* LOGS TAB */}
                {activeTab === 'logs' && (
                    <div className="absolute inset-0 overflow-auto p-2 space-y-1 font-mono text-[11px]">
                        {logs.length === 0 && <span className="text-gray-400 italic p-2">Ready to run...</span>}
                        {logs.map((log, i) => (
                            <div key={i} className={`flex gap-2 border-b border-gray-50/50 pb-0.5 last:border-0 hover:bg-gray-50 ${log.level === 'Error' ? 'text-red-600 bg-red-50/30' : 'text-gray-600'}`}>
                                <span className="text-gray-400 shrink-0 select-none">[{log.time}]</span>
                                <span className={log.level === 'Info' ? 'text-blue-600 font-bold' : (log.level === 'Warning' ? 'text-orange-500' : '')}>[{log.level}]</span>
                                <span>{log.message}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* STATS TAB */}
                {activeTab === 'stats' && (
                    <div className="absolute inset-0 overflow-auto p-4 grid grid-cols-4 gap-4">
                        {sortedStats.map(([nodeId, stats]: [string, any]) => {
                            const node = getNodes().find((n: any) => n.id === nodeId);
                            if (node?.type === 'annotation') return null;
                            return (
                                <div key={nodeId} className="bg-white border rounded-lg p-3 shadow-sm h-fit">
                                    <div className="flex items-center gap-2 mb-2 border-b pb-2">
                                        <div className="h-2 w-2 rounded-full bg-green-500" />
                                        <span className="font-semibold text-xs truncate max-w-[150px]">{node?.data?.label || nodeId}</span>
                                    </div>
                                    <div className="space-y-1 text-[10px] text-gray-500">
                                        <div className="flex justify-between"><span>Rows:</span> <span className="font-mono text-gray-900">{stats.rows?.toLocaleString() || '-'}</span></div>
                                        <div className="flex justify-between"><span>Cols:</span> <span className="font-mono text-gray-900">{stats.cols || '-'}</span></div>
                                        <div className="flex justify-between"><span>Time:</span> <span className="font-mono text-gray-900">{stats.duration || '0ms'}</span></div>
                                    </div>
                                </div>
                            );
                        })}
                        {Object.keys(nodeStats).length === 0 && <div className="col-span-4 text-center text-gray-400 py-8">Run the flow to see statistics.</div>}
                    </div>
                )}

                {/* DATA PREVIEW TAB */}
                {activeTab === 'preview' && (
                    <div className="absolute inset-0 flex flex-col">
                        {!selectedNodeId ? (
                            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 text-xs">
                                <Table className="h-8 w-8 mb-2 opacity-20" />
                                <p>Select a node in the canvas to see its output.</p>
                            </div>
                        ) : !currentPreview ? (
                            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 text-xs">
                                <Activity className="h-8 w-8 mb-2 opacity-20" />
                                <p>No data available for this node.</p>
                                <p className="text-[10px] opacity-70 mt-1">Run the flow to generate preview.</p>
                            </div>
                        ) : (
                            <div className="flex-1 overflow-auto">
                                <table className="w-full text-left border-collapse text-[11px]">
                                    <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                                        <tr>
                                            {showExplain && <th className="p-2 w-10 text-center bg-gray-50 border-b"></th>}
                                            {previewColumns.map((col, idx) => (
                                                <th key={`${col}-${idx}`} className="p-2 font-medium text-gray-600 border-b border-r border-gray-200 whitespace-nowrap bg-gray-50">
                                                    {col}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {currentPreview.map((row, i) => (
                                            <tr key={i} className="hover:bg-blue-50/30 group">
                                                {showExplain && (
                                                    <td className="p-1 border-r text-center w-8">
                                                        <button onClick={() => handleExplain(row)} className="text-gray-400 hover:text-purple-600 transition-colors p-1" title="Explain Prediction">
                                                            <Brain className="w-3.5 h-3.5" />
                                                        </button>
                                                    </td>
                                                )}
                                                {previewColumns.map((col, idx) => {
                                                    const cellVal = typeof row[col] === 'object' ? JSON.stringify(row[col]) : String(row[col] ?? '');
                                                    return (
                                                        <td
                                                            key={`${col}-${idx}`}
                                                            className="p-1.5 border-r border-gray-100 truncate max-w-[200px] text-gray-700 font-mono cursor-pointer hover:bg-blue-50/60"
                                                            title="Click to expand"
                                                            onClick={() => setCellModal({ column: col, value: cellVal })}
                                                        >
                                                            {cellVal}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {currentPreview && (
                            <div className="h-6 border-t border-gray-200 bg-gray-50 flex items-center px-2 text-[10px] text-gray-500 justify-between shrink-0">
                                <span>
                                    Showing {currentPreview.length} rows
                                    {previewColumns.length < allPreviewColumns.length && (
                                        <span className="ml-2 text-indigo-500 font-medium">
                                            · {previewColumns.length} / {allPreviewColumns.length} columns selected
                                        </span>
                                    )}
                                </span>
                                <span>{nodeName}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* I/O TAB */}
                {activeTab === 'io' && (
                    <div className="absolute inset-0 flex flex-col">
                        {!selectedNodeId ? (
                            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 text-xs">
                                <ArrowLeftRight className="h-8 w-8 mb-2 opacity-20" />
                                <p>Select a node to see its Input / Output schema.</p>
                                <p className="text-[10px] opacity-60 mt-1">No execution required — schemas are inferred at design time.</p>
                            </div>
                        ) : ioLoading ? (
                            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 text-xs">
                                <Loader2 className="h-6 w-6 animate-spin mb-2 text-blue-400" />
                                <p>Resolving schema...</p>
                            </div>
                        ) : ioError ? (
                            <div className="flex-1 flex flex-col items-center justify-center text-red-400 text-xs">
                                <AlertCircle className="h-6 w-6 mb-2" />
                                <p>{ioError}</p>
                            </div>
                        ) : (
                            <>
                                {/* Node Header */}
                                <div className="h-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-b px-4 flex items-center gap-2 shrink-0">
                                    <Columns3 className="h-3.5 w-3.5 text-indigo-500" />
                                    <span className="text-xs font-semibold text-indigo-800">{nodeName}</span>
                                    <span className="text-[10px] text-indigo-400 bg-indigo-100 px-1.5 py-0.5 rounded">{nodeType}</span>
                                    <div className="flex-1" />
                                    <button
                                        onClick={() => selectedNodeId && resolveIO(selectedNodeId)}
                                        className="text-[10px] text-indigo-600 hover:text-indigo-800 hover:underline"
                                    >
                                        ↻ Refresh
                                    </button>
                                </div>

                                {/* Split View: Input | Output */}
                                <div className="flex-1 flex overflow-hidden">
                                    {/* INPUT Panel */}
                                    <div className="flex-1 flex flex-col border-r border-gray-200 min-w-0">
                                        <div className="h-7 bg-gray-50 border-b px-3 flex items-center justify-between shrink-0">
                                            <div className="flex items-center gap-1.5">
                                                <div className="h-2 w-2 rounded-full bg-blue-400" />
                                                <span className="text-[11px] font-semibold text-gray-600">Input</span>
                                            </div>
                                            <span className="text-[10px] text-gray-400">{ioInput.length} column{ioInput.length !== 1 ? 's' : ''}</span>
                                        </div>
                                        <div className="flex-1 overflow-auto">
                                            {ioInput.length === 0 ? (
                                                <div className="flex items-center justify-center h-full text-gray-300 text-[11px]">
                                                    {nodeType === 'source' || nodeType === 'ediSource'
                                                        ? 'Source node — no input'
                                                        : 'No upstream schema found'}
                                                </div>
                                            ) : (
                                                <table className="w-full text-[11px]">
                                                    <thead className="bg-gray-50/70 sticky top-0">
                                                        <tr>
                                                            <th className="text-left px-3 py-1 font-medium text-gray-500 border-b">#</th>
                                                            <th className="text-left px-3 py-1 font-medium text-gray-500 border-b">Column</th>
                                                            <th className="text-left px-3 py-1 font-medium text-gray-500 border-b">Type</th>
                                                            {ioInput.some(c => c.source) && (
                                                                <th className="text-left px-3 py-1 font-medium text-gray-500 border-b">Source</th>
                                                            )}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {ioInput.map((col, i) => {
                                                            const isDropped = droppedCols.some(d => d.name === col.name);
                                                            return (
                                                                <tr key={i}
                                                                    className={`border-b border-gray-50 hover:bg-gray-50/50 ${isDropped ? 'bg-red-50/30' : ''}`}>
                                                                    <td className="px-3 py-1 text-gray-300 font-mono">{i + 1}</td>
                                                                    <td className={`px-3 py-1 font-mono ${isDropped ? 'line-through text-red-400' : 'text-gray-800'}`}>
                                                                        {col.name}
                                                                        {isDropped && <span className="ml-2 text-[9px] text-red-400 font-sans">dropped</span>}
                                                                    </td>
                                                                    <td className="px-3 py-1">
                                                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${typeBadgeColor(col.type)}`}>
                                                                            {col.type}
                                                                        </span>
                                                                    </td>
                                                                    {ioInput.some(c => c.source) && (
                                                                        <td className="px-3 py-1">
                                                                            {col.source && (
                                                                                <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${col.source === 'left' ? 'bg-sky-100 text-sky-700' : 'bg-violet-100 text-violet-700'}`}>
                                                                                    {col.source}
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                    )}
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    </div>

                                    {/* Arrow Divider */}
                                    <div className="w-8 flex items-center justify-center bg-gray-50/50 shrink-0">
                                        <ArrowRight className="h-4 w-4 text-gray-300" />
                                    </div>

                                    {/* OUTPUT Panel */}
                                    <div className="flex-1 flex flex-col min-w-0">
                                        <div className="h-7 bg-gray-50 border-b px-3 flex items-center justify-between shrink-0">
                                            <div className="flex items-center gap-1.5">
                                                <div className="h-2 w-2 rounded-full bg-emerald-400" />
                                                <span className="text-[11px] font-semibold text-gray-600">Output</span>
                                            </div>
                                            <span className="text-[10px] text-gray-400">{ioOutput.length} column{ioOutput.length !== 1 ? 's' : ''}</span>
                                        </div>
                                        <div className="flex-1 overflow-auto">
                                            {ioOutput.length === 0 ? (
                                                <div className="flex items-center justify-center h-full text-gray-300 text-[11px]">
                                                    No output schema resolved. Configure the node or its source.
                                                </div>
                                            ) : (
                                                <table className="w-full text-[11px]">
                                                    <thead className="bg-gray-50/70 sticky top-0">
                                                        <tr>
                                                            <th className="text-left px-3 py-1 font-medium text-gray-500 border-b">#</th>
                                                            <th className="text-left px-3 py-1 font-medium text-gray-500 border-b">Column</th>
                                                            <th className="text-left px-3 py-1 font-medium text-gray-500 border-b">Type</th>
                                                            <th className="text-left px-3 py-1 font-medium text-gray-500 border-b w-16">Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {ioOutput.map((col, i) => {
                                                            const status = diffMap.get(col.name) || 'unchanged';
                                                            return (
                                                                <tr key={i}
                                                                    className={`border-b border-gray-50 hover:bg-gray-50/50 ${status === 'added' ? 'bg-emerald-50/30' :
                                                                        status === 'renamed' ? 'bg-amber-50/30' :
                                                                            status === 'type_changed' ? 'bg-blue-50/30' : ''
                                                                        }`}>
                                                                    <td className="px-3 py-1 text-gray-300 font-mono">{i + 1}</td>
                                                                    <td className="px-3 py-1 font-mono text-gray-800">{col.name}</td>
                                                                    <td className="px-3 py-1">
                                                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${typeBadgeColor(col.type)}`}>
                                                                            {col.type}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-3 py-1">
                                                                        {status === 'renamed' && (
                                                                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">renamed</span>
                                                                        )}
                                                                        {status === 'added' && (
                                                                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">new</span>
                                                                        )}
                                                                        {status === 'type_changed' && (
                                                                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">retyped</span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Summary Footer */}
                                <div className="h-7 border-t border-gray-200 bg-gray-50 flex items-center px-4 text-[10px] text-gray-500 justify-between shrink-0">
                                    <div className="flex items-center gap-3">
                                        <span>In: <b className="text-gray-700">{ioInput.length}</b></span>
                                        <span>→</span>
                                        <span>Out: <b className="text-gray-700">{ioOutput.length}</b></span>
                                        {droppedCols.length > 0 && (
                                            <span className="text-red-500">({droppedCols.length} dropped)</span>
                                        )}
                                        {(ioOutput.length - ioInput.length) > 0 && (
                                            <span className="text-emerald-600">(+{ioOutput.length - ioInput.length} added)</span>
                                        )}
                                    </div>
                                    <span className="text-gray-400 italic">Design-time schema inference • No execution required</span>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* ERRORS TAB */}
                {activeTab === 'errors' && (
                    <div className="absolute inset-0 overflow-auto p-2 space-y-1 font-mono text-[11px]">
                        {logs.filter(l => l.level === 'Error').length === 0 && (
                            <div className="flex flex-col items-center justify-center h-full text-gray-400">
                                <AlertCircle className="h-8 w-8 mb-2 opacity-20" />
                                <p>No errors recorded.</p>
                            </div>
                        )}
                        {logs.filter(l => l.level === 'Error').map((log, i) => (
                            <div key={i} className="flex gap-2 border-b border-red-100 pb-2 last:border-0 bg-red-50 p-2 rounded border">
                                <span className="text-red-400 shrink-0 select-none">[{log.time}]</span>
                                <div className="flex flex-col gap-1">
                                    <span className="font-bold text-red-700">Execution Error</span>
                                    <span className="text-red-900 whitespace-pre-wrap">{log.message}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Cell Content Modal */}
            {cellModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setCellModal(null)}>
                    <div className="bg-white rounded-xl shadow-2xl w-[700px] max-w-[90vw] max-h-[80vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="h-11 border-b px-4 flex items-center justify-between bg-blue-50 shrink-0">
                            <div className="flex items-center gap-2 text-blue-800 font-medium text-sm">
                                <Table className="w-4 h-4" />
                                <span className="truncate max-w-[400px]">{cellModal.column}</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <CopyButton text={cellModal.value} />
                                <button onClick={() => setCellModal(null)} className="text-gray-400 hover:text-gray-800 p-1 rounded hover:bg-gray-100">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-auto p-4">
                            <pre className="whitespace-pre-wrap break-words text-[12px] text-gray-800 font-mono leading-relaxed">{cellModal.value}</pre>
                        </div>
                    </div>
                </div>
            )}

            {/* Explain Modal */}
            {isExplainOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-lg shadow-xl w-[500px] max-h-[80vh] flex flex-col overflow-hidden">
                        <div className="h-12 border-b px-4 flex items-center justify-between bg-purple-50">
                            <div className="flex items-center gap-2 text-purple-800 font-medium">
                                <Brain className="w-4 h-4" />
                                <span>Prediction Logic</span>
                            </div>
                            <button onClick={() => setIsExplainOpen(false)} className="text-gray-400 hover:text-gray-800"><Minimize2 className="w-4 h-4 rotate-45" /></button>
                        </div>
                        <div className="p-6 overflow-y-auto">
                            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-4">Top Factors Influencing this Prediction</h4>
                            <div className="space-y-4">
                                {explainResult?.map((item: any, i: number) => (
                                    <div key={i} className="space-y-1">
                                        <div className="flex justify-between text-sm">
                                            <span className="font-medium text-gray-700">{item.Feature}</span>
                                            <span className="text-gray-500 font-mono">{(item.Importance * 100).toFixed(1)}% Impact</span>
                                        </div>
                                        <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-purple-500 rounded-full"
                                                style={{ width: `${Math.min(item.Importance * 1000, 100)}%` }}
                                            />
                                        </div>
                                    </div>
                                ))}
                                {(!explainResult || explainResult.length === 0) && <p className="text-gray-400 italic text-sm">No significant factors found.</p>}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function TabButton({ id, active, onClick, icon: Icon, label, count, isCompact }: any) {
    return (
        <button
            id={id}
            onClick={onClick}
            title={isCompact ? label : undefined}
            className={`
                h-full px-3 flex items-center gap-2 text-[11px] font-medium border-b-2 transition-all
                ${active ? 'border-blue-500 text-blue-600 bg-white' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100'}
            `}
        >
            <Icon className={`h-3.5 w-3.5 shrink-0 ${active ? 'text-blue-500' : 'text-gray-400'}`} />
            {!isCompact && <span>{label}</span>}
            {count > 0 && <span className="bg-red-100 text-red-600 px-1.5 rounded-full text-[9px] min-w-[16px] max-w-[24px] truncate text-center shrink-0">{count}</span>}
        </button>
    );
}

function CopyButton({ text }: { text: string }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };
    return (
        <button onClick={handleCopy} className="text-gray-400 hover:text-blue-600 p-1 rounded hover:bg-gray-100" title="Copy to clipboard">
            {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
        </button>
    );
}
